// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { ApiBase, type ApiRequestConfig, setAccessToken, clearLegacyLocalStorageToken } from './apiBase';

export class ApiAuth extends ApiBase {
  // Auth
  async login(email: string, password: string) {
    const response = await this.client.post('/v1/auth/login', { email, password });
    const { access_token } = response.data;
    // ADR-025: store token via the unified token contract
    setAccessToken(access_token);
    clearLegacyLocalStorageToken();
    return response.data;
  }

  // 访问链接直达登录（比赛评委场景）：/login?tk=<access-link token>，
  // 网址即钥匙，兑换响应与 /login 同构（access_token + user）。
  async loginWithAccessLink(token: string) {
    const response = await this.client.post('/v1/auth/access-link/login', { token });
    const { access_token } = response.data;
    setAccessToken(access_token);
    clearLegacyLocalStorageToken();
    return response.data;
  }

  async register(email: string, password: string, name: string) {
    return this.client.post('/v1/auth/register', { email, password, name });
  }

  async verifyEmail(token: string) {
    return this.client.get('/v1/auth/verify-email', { params: { token } });
  }

  async verifyCode(email: string, code: string) {
    return this.client.post('/v1/auth/verify-code', { email, code });
  }

  async sendCode(email: string) {
    return this.client.post('/v1/auth/send-code', { email });
  }

  async resendVerification(email: string) {
    return this.client.post('/v1/auth/resend-verification', { email });
  }

  async getProfile() {
    return this.client.get('/v1/auth/me', {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async getSocialProviders() {
    return this.client.get('/v1/auth/social-providers', {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async sendSmsCode(phone: string) {
    return this.client.post('/v1/auth/send-sms-code', { phone });
  }

  async bindPhone(phone: string, code: string) {
    const response = await this.client.post('/v1/auth/bind-phone', { phone, code });
    if (response.data.access_token) {
      // ADR-025: store token via the unified token contract
      setAccessToken(response.data.access_token);
      clearLegacyLocalStorageToken();
    }
    return response.data;
  }

  async forgotPassword(email: string) {
    return this.client.post('/v1/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string) {
    return this.client.post('/v1/auth/reset-password', { token, new_password: newPassword });
  }

  async changePasswordSendCode(oldPassword: string) {
    return this.client.post('/v1/auth/change-password/send-code', { old_password: oldPassword });
  }

  async changePassword(oldPassword: string, newPassword: string, code: string) {
    return this.client.post('/v1/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
      code,
    });
  }

  async updateProfile(data: { name?: string }) {
    return this.client.patch('/v1/auth/profile', data);
  }

  logout() {
    void this.client
      .post('/v1/auth/logout', undefined, {
        suppressErrorToast: true,
        suppressUnauthorizedRedirect: true,
      } as ApiRequestConfig)
      .catch(() => undefined);
    // ADR-025: clear token via the unified token contract
    setAccessToken(null);
  }

  // Subscription & Payment
  async getCommerceSettings() {
    return this.client.get('/v1/payment/commerce', {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async getPlans() {
    return this.client.get('/v1/payment/plans');
  }

  async getMySubscription() {
    return this.client.get('/v1/payment/subscription/me', {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async createOrder(
    planCode: string,
    billingCycle: string = 'monthly',
    paymentMethod: string = 'xunhupay'
  ) {
    return this.client.post('/v1/payment/orders', {
      plan_code: planCode,
      billing_cycle: billingCycle,
      payment_method: paymentMethod,
    });
  }

  async getOrder(orderNo: string) {
    return this.client.get(`/v1/payment/orders/${orderNo}`);
  }

  async getMyOrders() {
    return this.client.get('/v1/payment/orders');
  }

  async simulatePayment(orderNo: string) {
    return this.client.post(`/v1/payment/dev/simulate-payment/${orderNo}`);
  }

  async getMyUsage() {
    return this.client.get('/v1/payment/usage', {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  // Sessions
  async getSessions(page = 1, page_size = 20) {
    return this.client.get('/v1/sessions/', {
      params: { page, page_size },
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async getSession(sessionId: string) {
    return this.client.get(`/v1/sessions/${sessionId}`, {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async createSession(title?: string, metadata?: Record<string, unknown>) {
    return this.client.post('/v1/sessions/', { title, metadata });
  }

  async deleteSession(sessionId: string) {
    return this.client.delete(`/v1/sessions/${sessionId}`);
  }

  async updateSession(
    sessionId: string,
    data: { title?: string; metadata?: Record<string, unknown> }
  ) {
    return this.client.patch(`/v1/sessions/${sessionId}`, data);
  }
}
